import { formatDate, formatTime } from "@/utils/date";
import LogoArSvg from "@/components/LogoArSvg";

export type QRInvoiceData = {
  type: "subscription" | "service" | "coupon";
  qrKey: string;
  date: string;
  time: string;
  // للاشتراكات
  userName?: string;
  phone?: string;
  serviceName?: string;
  size?: string;
  remainingWashes?: number;
  endDate?: string;
  // للخدمات
  pointsSpent?: number;
  endAt?: string;
  // للكوبونات
  code?: string;
  discount?: number;
};

const QRInvoicePrintContent = ({ data }: { data: QRInvoiceData }) => {
  const currentDate = formatDate(new Date().toISOString());
  const currentTime = formatTime(new Date().toISOString());

  return (
    <div
      dir='rtl'
      style={{
        width: "58mm",
        padding: "5mm",
        background: "#fff",
        color: "#000",
        border: "none",
        borderRadius: "8px",
        fontFamily: "Arial",
        fontSize: "14px",
        boxShadow: "none",
      }}
    >
      <style>{`
        *, *:focus { outline: none !important; -webkit-tap-highlight-color: transparent !important; }
        @media print {
          * {
            font-size: 14px !important;
            color: black !important;
            outline: none !important;
            -webkit-tap-highlight-color: transparent !important;
          }
          table, th, td {
            border: 1px solid black !important;
          }
          body {
            margin: 0 !important;
            padding: 0 !important;
            background: white !important;
          }
        }
        .title { text-align: center; margin-bottom: 12px; font-weight: bold; font-size: 18px; }
        .invoice-section { border-top: 1px solid #ccc; padding-top: 8px; margin-top: 8px; }
        .flex-between { display: flex; justify-content: space-between; align-items: center; margin-bottom: 4px; }
        .font-bold { font-weight: bold; }
        .font-normal { font-weight: normal; }
        .mt-4 { margin-top: 16px; }
        .footer { margin-top: 16px; text-align: center; }
        .space-y-1 > * + * { margin-top: 4px; }
        .status { margin-top: 16px; text-align: center; border-top: 1px solid #ccc; padding-top: 8px; }
        .status-box { background: #d1fae5; color: #065f46; padding: 8px; border-radius: 6px; font-weight: bold; }
      `}</style>

      {/* Title */}
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          marginBottom: 8,
          marginTop: 10,
        }}
      >
        <LogoArSvg style={{ maxHeight: 48, height: 48 }} />
      </div>
      <div className='title'>
        {data.type === "subscription" && "إيصال استخدام اشتراك"}
        {data.type === "service" && "إيصال استخدام خدمة"}
        {data.type === "coupon" && "إيصال استخدام كوبون"}
      </div>

      {/* QR Info */}
      <div className='invoice-section space-y-1'>
        <div className='flex-between'>
          <span>كود QR:</span>
          <span className='font-bold'>{data.qrKey}</span>
        </div>
        <div className='flex-between'>
          <span>التاريخ:</span>
          <span>{currentDate}</span>
        </div>
        <div className='flex-between'>
          <span>الوقت:</span>
          <span>{currentTime}</span>
        </div>
      </div>

      {/* Subscription Details */}
      {data.type === "subscription" && (
        <div className='mt-4 invoice-section space-y-1'>
          <div className='font-bold' style={{ marginBottom: 8 }}>
            تفاصيل الاشتراك:
          </div>
          <div className='flex-between'>
            <span>اسم العميل:</span>
            <span>{data.userName}</span>
          </div>
          <div className='flex-between'>
            <span>رقم الهاتف:</span>
            <span>{data.phone}</span>
          </div>
          <div className='flex-between'>
            <span>نوع الخدمة:</span>
            <span>{data.serviceName}</span>
          </div>
          <div className='flex-between'>
            <span>الحجم:</span>
            <span>{data.size}</span>
          </div>
          <div className='flex-between'>
            <span>الغسلات المتبقية:</span>
            <span className='font-bold'>{data.remainingWashes} غسلة</span>
          </div>
          <div className='flex-between'>
            <span>تاريخ الانتهاء:</span>
            <span>{data.endDate}</span>
          </div>
        </div>
      )}

      {/* Service Details */}
      {data.type === "service" && (
        <div className='mt-4 invoice-section space-y-1'>
          <div className='font-bold' style={{ marginBottom: 8 }}>
            تفاصيل الخدمة:
          </div>
          <div className='flex-between'>
            <span>نوع الخدمة:</span>
            <span>{data.serviceName}</span>
          </div>
          <div className='flex-between'>
            <span>الحجم:</span>
            <span>{data.size}</span>
          </div>
          <div className='flex-between'>
            <span>النقاط المستخدمة:</span>
            <span>{data.pointsSpent}</span>
          </div>
          <div className='flex-between'>
            <span>ينتهي في:</span>
            <span>{data.endAt}</span>
          </div>
        </div>
      )}

      {/* Coupon Details */}
      {data.type === "coupon" && (
        <div className='mt-4 invoice-section space-y-1'>
          <div className='font-bold' style={{ marginBottom: 8 }}>
            تفاصيل الكوبون:
          </div>
          <div className='flex-between'>
            <span>كود الخصم:</span>
            <span>{data.code}</span>
          </div>
          <div className='flex-between'>
            <span>نسبة الخصم:</span>
            <span className='font-bold'>{data.discount}%</span>
          </div>
        </div>
      )}

      {/* Status */}
      <div className='status'>
        <div className='status-box'>
          <p>تم الاستخدام بنجاح ✓</p>
        </div>
      </div>

      {/* Footer */}
      <div className='footer'>
        <p>شكراً لتعاملكم معنا</p>
        <p style={{ marginTop: 4 }}>Netlos</p>
        <hr
          style={{ width: "90%", borderTop: "1px dashed #000", marginTop: 8 }}
        />
        <div style={{ textAlign: "center", marginTop: 6 }}>
          تم التطوير من خلال شركة اوريجامي
        </div>
        <div style={{ textAlign: "center", marginTop: 2 }}>origami.sa</div>
      </div>
    </div>
  );
};

export default QRInvoicePrintContent;
